import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCredibilityThunk,
  fetchCredHistoryThunk,
  fetchCredLeaderboardThunk,
  enqueueCredRecomputeThunk,
  recomputeCredSyncThunk,
  fetchGroupCredibility,
} from "./credibilityActionThunks";

const initialState = {
  byUser: {},           // { [userId]: { data, status } }  status: "ready" | "pending-202"
  historyByUser: {},    // { [userId]: [snapshots] }
  leaderboard: [],      // array of CredibilityResponse (cheap; sectors omitted server-side)
  groupScore: null,
  loading: false,
  error: null,
  enqueueStatus: null,  // "success" | "error" | null
  recomputeStatus: null // "success" | "error" | null
};

const credibilitySlice = createSlice({
  name: "credibility",
  initialState,
  reducers: {
    resetCredibilityState: (state) => {
      state.loading = false;
      state.error = null;
      state.enqueueStatus = null;
      state.recomputeStatus = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch current credibility
    builder
      .addCase(fetchCredibilityThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCredibilityThunk.fulfilled, (state, { payload }) => {
        const { userId, data, status } = payload;
        state.byUser[userId] = {
          data,
          status: status === 202 ? "pending-202" : "ready",
        };
        state.loading = false;
      })
      .addCase(fetchCredibilityThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Fetch history
    builder
      .addCase(fetchCredHistoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCredHistoryThunk.fulfilled, (state, { payload }) => {
        const { userId, history } = payload;
        state.historyByUser[userId] = history || [];
        state.loading = false;
      })
      .addCase(fetchCredHistoryThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Leaderboard
    builder
      .addCase(fetchCredLeaderboardThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCredLeaderboardThunk.fulfilled, (state, { payload }) => {
        state.leaderboard = payload.list || [];
        state.loading = false;
      })
      .addCase(fetchCredLeaderboardThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Enqueue recompute (async)
    builder
      .addCase(enqueueCredRecomputeThunk.pending, (state) => {
        state.enqueueStatus = null;
        state.error = null;
      })
      .addCase(enqueueCredRecomputeThunk.fulfilled, (state) => {
        state.enqueueStatus = "success";
      })
      .addCase(enqueueCredRecomputeThunk.rejected, (state, { payload }) => {
        state.enqueueStatus = "error";
        state.error = payload;
      });

    // Sync recompute (admin)
    builder
      .addCase(recomputeCredSyncThunk.pending, (state) => {
        state.recomputeStatus = null;
        state.error = null;
      })
      .addCase(recomputeCredSyncThunk.fulfilled, (state, { payload }) => {
        const { userId, data } = payload;
        state.byUser[userId] = { data, status: "ready" };
        state.recomputeStatus = "success";
      })
      .addCase(recomputeCredSyncThunk.rejected, (state, { payload }) => {
        state.recomputeStatus = "error";
        state.error = payload;
      });
  
      // Group credibility
      builder
        .addCase(fetchGroupCredibility.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchGroupCredibility.fulfilled, (state, { payload }) => {
          state.groupScore = payload; // store the GroupCredibilityResponse directly
          state.loading = false;
        })
        .addCase(fetchGroupCredibility.rejected, (state, { payload }) => {
          state.loading = false;
          state.error = payload;
        });
      }
});

export const { resetCredibilityState } = credibilitySlice.actions;
export default credibilitySlice.reducer;

// --- Optional selectors ---
export const selectCredByUser = (state, userId) => state.credibility.byUser[userId]?.data || null;
export const selectCredStatus = (state, userId) => state.credibility.byUser[userId]?.status || "unknown";
export const selectCredHistory = (state, userId) => state.credibility.historyByUser[userId] || [];
export const selectCredLeaderboard = (state) => state.credibility.leaderboard || [];
export const selectGroupCredibility = (state) => state.credibility.groupScore;
